"use client";

import { useFindRepsFromLocationQuery } from "@/Redux/apis/reps";
import Image from "next/image";
import React from "react";
import Avatar from "react-avatar";
import { sanitizeImageUrl } from "@/utils/imageUrl";
import { useSelector } from "react-redux";
import { withAlphaMdDefaultRep } from "./defaultRep";
import AlphaMdRepRequestPopup from "./AlphaMdRepRequestPopup";

const RepsImage = ({ product, isLoggedIn, reps: serverReps }) => {
  const [showAlphaRequest, setShowAlphaRequest] = React.useState(false);
  const users = useSelector((state) => state?.userData?.userData?.user);
  const isUserLoggedIn = isLoggedIn || !!users;

  // GeoJSON coordinates: [longitude, latitude]
  const coords = users?.location?.coordinates;
  const userLat = coords?.[1] || 0;
  const userLng = coords?.[0] || 0;

  // Skip client-side fetch if server already provided reps data
  const hasServerReps = serverReps?.length > 0;
  const { data: repsData, isLoading: repsLoading } = useFindRepsFromLocationQuery(
    {
      productId: product?.id,
      latitude: userLat,
      longitude: userLng,
    },
    { skip: !isUserLoggedIn || !product?.id || hasServerReps }
  );

  const resolvedReps = hasServerReps
    ? serverReps
    : (repsData?.data?.length > 0 ? repsData.data : serverReps);
  const reps = withAlphaMdDefaultRep(resolvedReps);
  const isShowingDefaultRep = reps.length === 1 && reps[0]?.isAlphaMdDefault;

  // Logged in — loading skeleton while fetching reps (skip if server already provided reps)
  if (isUserLoggedIn && repsLoading && !hasServerReps) {
    return (
      <div className="flex items-center">
        <div className="flex items-center space-x-2">
          <span className="font-normal text-[#4B4B4B] text-[14px]">Reps:</span>
          <div className="flex -space-x-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="w-[28px] h-[28px] rounded-full bg-gray-200 animate-pulse border-2 border-white" />
            ))}
          </div>
          {/* <div className="w-16 h-3 bg-gray-200 animate-pulse rounded" /> */}
        </div>
      </div>
    );
  }

  // Logged in with reps available — show rep avatars
  if (isUserLoggedIn && reps?.length > 0) {
    return (
      <>
      <div className="flex items-center">
        <div className="flex items-center space-x-2">
          <span className="font-normal text-[#4B4B4B] text-[14px]">Reps:</span>
          <a
            href={isShowingDefaultRep ? undefined : "#reps"}
            onClick={(event) => {
              if (isShowingDefaultRep) {
                event.preventDefault();
                setShowAlphaRequest(true);
              }
            }}
            aria-label={isShowingDefaultRep ? "Request an Alpha.MD representative" : "View representatives"}
            className="flex -space-x-3 cursor-pointer"
          >
            {reps.slice(0, 4).map((rep, index) => (
              <div
                key={index}
                className={`relative w-[28px] h-[28px] shrink-0 flex items-center justify-center ${
                  rep.isAlphaMdDefault ? "overflow-visible" : "rounded-full overflow-hidden"
                }`}
              >
                {sanitizeImageUrl(rep.profile) ? (
                  <Image
                    src={rep.profile}
                    alt={rep.isAlphaMdDefault ? "Alpha.MD Representative" : `Rep ${index + 1}`}
                    width={28}
                    height={28}
                    className={rep.isAlphaMdDefault
                      ? "w-[26px] h-[26px] object-contain"
                      : "w-full h-full rounded-full border border-white object-cover"
                    }
                  />
                ) : (
                  // size matches the 28px container so the initials avatar
                  // sits flush with the photo avatars in the overlap row —
                  // otherwise the photo (w-full h-full → 28) renders bigger
                  // than the initials chip (was size=24).
                  <Avatar name={rep.repDetails?.firstName} color="#354EF0" size="28" round textSizeRatio={1.75} />
                )}
              </div>
            ))}
          </a>
          {reps.length > 4 && (
            <a href="#reps" className="text-[#354EF0] cursor-pointer text-[12px] font-medium underline">
              {reps.length - 4} more reps
            </a>
          )}
        </div>
      </div>
      {showAlphaRequest && <AlphaMdRepRequestPopup product={product} onClose={() => setShowAlphaRequest(false)} />}
      </>
    );
  }

  // Not logged in — show placeholder avatars with signup tooltip
  return (
    <div className="flex items-center">
      <div className="flex items-center space-x-2">
        <span className="font-normal text-[#4B4B4B] text-[14px]">Reps:</span>
        <a href="#reps" className="flex -space-x-3">
          {Array.from({ length: 2 }).map((_, index) => (
            <div key={index} className="relative group w-[28px] h-[28px] rounded-full">
              <Image
                src={"/Images/user-placeholder.svg"}
                alt={`Rep ${index + 1}`}
                width={24}
                height={24}
                className="w-full h-full rounded-full border border-white"
              />
              <span className="absolute top-8 left-1/2 transform -translate-x-1/2 scale-0 group-hover:scale-100 transition-all w-[240px] text-center rounded-full bg-[#354EF0] text-white text-[14px] font-medium px-2 py-2">
                Signup or Login to view Reps
              </span>
            </div>
          ))}
        </a>
      </div>
    </div>
  );
};

export default RepsImage;
